/**
 * Debug stack trace utilities for Reflux memory tracking.
 * Provides reusable hooks and functions for capturing component-level
 * stack traces that can be used by any hook that subscribes to stores.
 *
 * @module reflux/monitoring-hooks
 */

import { useRef } from 'react';

/**
 * Hook to capture debug stack trace during render.
 * Call this at the top of any hook that will subscribe to stores.
 * The stack is captured during render (when the calling component IS on the call stack),
 * not during useEffect (when only React internals are on the stack).
 *
 * Only captures when memory monitoring is enabled to avoid performance impact.
 *
 * @returns {React.MutableRefObject<string|null>} Ref containing captured stack, or null if disabled
 *
 * @example
 * function useMyStoreHook(store) {
 *   const debugStack = useDebugStack();
 *
 *   useEffect(() => {
 *     setDebugStackForNextSubscription(debugStack);
 *     const unsubscribe = store.listen(callback);
 *     return unsubscribe;
 *   }, []);
 * }
 */
export function useDebugStack() {
  const stackRef = useRef(null);

  // Only capture on first render, and only when monitoring is enabled
  if (
    stackRef.current === null &&
    typeof window !== 'undefined' &&
    window.__REFLUX_MONITORING__?.isEnabled?.()
  ) {
    stackRef.current = new Error().stack;
  }

  return stackRef;
}

/**
 * Capture debug stack trace for non-hook contexts (HOCs, class components).
 * Call this at the point where the calling code IS on the stack.
 * Returns the captured stack string, or null if monitoring is disabled.
 *
 * @returns {string|null} Captured stack, or null if disabled
 *
 * @example
 * function connectToStores(Component, config) {
 *   // Capture stack NOW - the file using connectToStores IS on the stack
 *   const capturedStack = captureDebugStack();
 *
 *   return class extends React.Component {
 *     componentDidMount() {
 *       setDebugStackForNextSubscription(capturedStack);
 *       store.listen(callback);
 *     }
 *   };
 * }
 */
export function captureDebugStack() {
  if (
    typeof window !== 'undefined' &&
    window.__REFLUX_MONITORING__?.isEnabled?.()
  ) {
    return new Error().stack;
  }
  return null;
}

/**
 * Set the debug stack to be used for the next store subscription.
 * Call this immediately before store.listen() within useEffect or componentDidMount.
 *
 * The stack will be consumed by the next trackSub() call and cleared,
 * so each subscription gets its own stack trace.
 *
 * @param {React.MutableRefObject<string|null>|string|null} stackOrRef - Ref from useDebugStack() or raw string from captureDebugStack()
 *
 * @example
 * // With hooks (using ref)
 * useEffect(() => {
 *   setDebugStackForNextSubscription(debugStack);
 *   const unsubscribe = store.listen(handleChange);
 *   return unsubscribe;
 * }, []);
 *
 * @example
 * // With class components (using raw string)
 * componentDidMount() {
 *   setDebugStackForNextSubscription(capturedStack);
 *   store.listen(callback);
 * }
 */
export function setDebugStackForNextSubscription(stackOrRef) {
  // Handle both ref objects (from hooks) and raw strings (from HOCs)
  const stack = stackOrRef?.current ?? stackOrRef;
  if (
    stack &&
    typeof window !== 'undefined' &&
    window.__REFLUX_MONITORING__?.setNextSubscriptionStack
  ) {
    window.__REFLUX_MONITORING__.setNextSubscriptionStack(stack);
  }
}
